import React, { useCallback, useRef, forwardRef, useEffect } from 'react';
import { Text, Rect, Group } from 'react-konva';
import { Node } from '../../core/models/scene';
import { useThemeAwareColor } from '../../react-app/theme/ThemeProvider';

interface StickyNoteNodeProps {
  node: Node;
  selected: boolean;
  editing?: boolean;
  canWrite?: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onStartTextEdit?: (textNode: any) => void;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
  onTransform?: (e: any) => void;
}

const StickyNoteNodeInner = ({
  node,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
  onStartTextEdit,
  editing,
  onTransformStart,
  onTransformEnd,
  canWrite = true
}: StickyNoteNodeProps, ref: React.Ref<any>) => {
  const getThemeColor = useThemeAwareColor();
  const groupRef = useRef<any>(null);
  const textRef = useRef<any>(null);
  const boundsRef = useRef<any>(null);
  const bgRectRef = useRef<any>(null);

  const handleDragStart = (e: any) => {
    const pos = e.target.position();
    onDragStart?.({ x: pos.x, y: pos.y });
  };

  const handleDragMove = (e: any) => {
    const pos = e.target.position();
    onDragMove?.({ x: pos.x, y: pos.y });
  };

  const handleDragEnd = (e: any) => {
    const pos = e.target.position();
    onDragEnd?.({ x: pos.x, y: pos.y });
  };

  const handleDoubleClick = (e: any) => {
    e.cancelBubble = true;
    onDoubleClick?.(e);
    if (onStartTextEdit && textRef.current) onStartTextEdit(textRef.current);
  };

  // Resolve style from node and attrs for compatibility with style commands
  const attrs: any = (node as any)?.attrs || {};
  const fontSize = attrs.fontSize ?? (node as any).fontSize ?? 16;
  const fontFamily = attrs.fontFamily ?? (node as any).fontFamily ?? 'Arial';
  const textColor = attrs.textColor ?? '#0F172A';
  const hAlign = (attrs.textAlign as any) || 'center';
  const vAlign = (attrs.verticalAlign as any) || 'middle';
  
  // Background styling
  const bgFill = (node as any).fillColor || '#FEF3C7';
  const bgStroke = (node as any).strokeColor || '#FCD34D';
  const bgStrokeWidth = typeof (node as any).strokeWidth === 'number' ? (node as any).strokeWidth : (typeof attrs.strokeWidth === 'number' ? attrs.strokeWidth : 0.8);
  const rectDash = Array.isArray(attrs.dash) ? attrs.dash : [];
  const rectDashEnabled = !!attrs.dashEnabled && rectDash.length > 0;

  // Auto-fit height to text content after text or width/style changes (same as TextNode)
  // But for sticky notes, we need to use the full container height for proper alignment
  useEffect(() => {
    const t: any = textRef.current;
    const r: any = boundsRef.current;
    const g: any = groupRef.current;
    if (!t || !r || !g) return;

    try {
      const boxWidth = Math.max(20, (node.width as number) || 100);
      // Get current rendered height or fallback to stored height
      // On initial mount, bounds might not have height set yet, so use stored node.height
      const storedHeight = (node.height as number) || 30;
      const renderedHeight = typeof r.height === 'function' ? r.height() : null;
      const currentRenderedHeight = (typeof renderedHeight === 'number' && renderedHeight > 0) ? renderedHeight : storedHeight;
      const lineH = (t.lineHeight?.() || 1.2) * (fontSize as number);

      // Build a measuring canvas 2D context
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const fw = (attrs.fontWeight || '').toString().toLowerCase();
      const fs = (attrs.fontStyle || '').toString().toLowerCase();
      const isBold = fw === 'bold' || fw === '700';
      const isItalic = fs === 'italic';
      const fontStyle = `${isItalic ? 'italic ' : ''}${isBold ? '700 ' : '400 '}${fontSize}px ${fontFamily}`;
      ctx.font = fontStyle;

      const measure = (s: string) => ctx.measureText(s).width;

      const wrapParagraph = (para: string) => {
        // Break by words; fallback to char-wrap for very long tokens
        const words = para.split(/(\s+)/); // keep spaces tokens
        let current = '';
        let lines = 0;
        for (const token of words) {
          if (token === '') continue;
          const next = current + token;
          if (measure(next) <= boxWidth || current === '') {
            current = next;
          } else {
            // commit current line
            lines += 1;
            current = token.trimStart();
            // If single token is wider than box, break by characters
            if (measure(current) > boxWidth) {
              let buf = '';
              for (const ch of current) {
                const trial = buf + ch;
                if (measure(trial) <= boxWidth || buf === '') buf = trial;
                else { lines += 1; buf = ch; }
              }
              current = buf;
            }
          }
        }
        if (current.length > 0) lines += 1;
        return lines;
      };

      const paragraphs = (String(node.text || 'Text')).split('\n');
      let totalLines = 0;
      for (const p of paragraphs) {
        if (p.trim().length === 0) {
          // Empty line - still counts as one line
          totalLines += 1;
        } else {
          totalLines += wrapParagraph(p);
        }
      }
      // Add buffer that scales with font size to prevent clipping
      // Larger fonts need more buffer for descenders, subpixel rendering, line spacing, etc.
      // Use a more generous buffer: at least 6px or 10% of font size, whichever is larger
      const buffer = Math.max(6, Math.ceil(fontSize * 0.1) + 4);
      // Also ensure each line has adequate height - add extra space per line for larger fonts
      const perLineBuffer = totalLines > 1 ? Math.ceil(fontSize * 0.05) : 0;
      const contentHeight = Math.max(18, Math.ceil(totalLines * lineH) + buffer + (perLineBuffer * totalLines));
      
      // For sticky notes: container should grow if content needs more space (to prevent clipping)
      // Always use at least contentHeight to prevent clipping (critical after refresh when stored height might be stale)
      // But preserve manual resizes if user made it larger (don't shrink)
      // This ensures text never clips, even after refresh when stored height might be stale
      const finalHeight = Math.max(currentRenderedHeight, contentHeight);

      // Apply height to text (use final height for alignment)
      const prevH = t.height();
      t.height(finalHeight);
      // Update bounds, group, and background to match final height
      if (typeof r.height === 'function') r.height(finalHeight);
      if (typeof g.height === 'function') g.height(finalHeight);
      // Update background rect height too
      const bg: any = g.findOne?.('.sticky-rect');
      if (bg && typeof bg.height === 'function') {
        bg.height(finalHeight);
        bg.clearCache?.();
      }
      g.scale({ x: 1, y: 1 });
      if (finalHeight !== prevH || finalHeight !== r.height?.()) {
        t.getLayer()?.batchDraw();
      }
    } catch {}
  }, [node.text, node.width, node.height, fontSize, fontFamily, attrs.fontWeight, attrs.fontStyle, attrs.textDecoration]);

  // Cache background for performance
  useEffect(() => {
    const rect = bgRectRef.current;
    if (!rect) return;
    try {
      if (editing) {
        rect.clearCache();
        rect.getLayer()?.batchDraw();
        return;
      }
      rect.clearCache();
      rect.cache({ pixelRatio: 1 });
      rect.getLayer()?.batchDraw();
    } catch {}
  }, [node.width, node.height, bgFill, bgStroke, bgStrokeWidth, rectDashEnabled, JSON.stringify(rectDash), editing]);

  return (
    <>
      <Group
        id={node.id}
        name="shape"
        ref={useCallback((n: any) => {
          groupRef.current = n;
          if (typeof ref === 'function') ref(n);
          else if (ref && typeof (ref as any) === 'object') (ref as any).current = n;
        }, [ref])}
        x={node.x}
        y={node.y}
        width={node.width || 100}
        height={node.height || 30}
        draggable={canWrite && !editing}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onClick={onClick}
        onDblClick={handleDoubleClick}
        onTransformStart={onTransformStart as any}
        onTransformEnd={onTransformEnd as any}
        onTransform={(e: any) => {
          const target = e.target;
          const scaleX = target.scaleX();
          const scaleY = target.scaleY();
          const curW = (boundsRef.current?.width?.() as number) || (node.width || 100);
          const curH = (boundsRef.current?.height?.() as number) || (node.height || 30);
          const nextW = Math.max(20, curW * scaleX);
          const nextH = Math.max(18, curH * scaleY);
          // Apply live size updates to bounds + text (exactly like TextNode)
          try { boundsRef.current?.width?.(nextW); } catch {}
          try { boundsRef.current?.height?.(nextH); } catch {}
          try { textRef.current?.width?.(nextW); } catch {}
          try { textRef.current?.height?.(nextH); } catch {}
          try { groupRef.current?.width?.(nextW); } catch {}
          try { groupRef.current?.height?.(nextH); } catch {}
          // Update background to match
          try { bgRectRef.current?.width?.(nextW); } catch {}
          try { bgRectRef.current?.height?.(nextH); } catch {}
          try { bgRectRef.current?.clearCache?.(); } catch {}
          // Keep group scale at 1 so text glyphs don't scale; size is applied above
          try { target.scaleX(1); target.scaleY(1); } catch {}
        }}
      >
        {/* Background rectangle (behind text) */}
        <Rect
          ref={bgRectRef}
          x={0}
          y={0}
          width={node.width || 100}
          height={node.height || 30}
          name="sticky-rect"
          listening={false}
          fill={bgFill}
          stroke={bgStroke}
          strokeWidth={bgStrokeWidth}
          strokeEnabled={bgStrokeWidth > 0}
          dashEnabled={rectDashEnabled}
          dash={rectDashEnabled ? rectDash : undefined}
          cornerRadius={2}
          shadowColor="rgba(0, 0, 0, 0.3)"
          shadowBlur={8}
          shadowOffsetX={0}
          shadowOffsetY={4}
          shadowOpacity={0.6}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />
        
        {/* Bounding rectangle (transformable) - same as TextNode, but needs to listen for drag */}
        <Rect
          x={0}
          y={0}
          width={node.width || 100}
          height={node.height || 30}
          name="text-bounds"
          ref={boundsRef}
          listening={true}
          fill="rgba(0,0,0,0)"
          stroke="rgba(0,0,0,0)"
          strokeWidth={0}
          strokeEnabled={false}
          hitStrokeWidth={0}
        />
        
        {/* Text - same as TextNode */}
        <Text
          ref={textRef}
          text={node.text || 'Text'}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fill={getThemeColor(textColor, { type: 'foreground' })}
          fontStyle={(() => {
            const fw = (attrs.fontWeight || '').toString().toLowerCase();
            const fs = (attrs.fontStyle || '').toString().toLowerCase();
            const isBold = fw === 'bold';
            const isItalic = fs === 'italic';
            if (isBold && isItalic) return 'bold italic';
            if (isBold) return 'bold';
            if (isItalic) return 'italic';
            return 'normal';
          })()}
          textDecoration={attrs.textDecoration || 'none'}
          width={node.width || 100}
          height={node.height || 30}
          align={hAlign}
          verticalAlign={vAlign}
          visible={!editing}
          wrap="word"
        />
      </Group>
    </>
  );
};

export const StickyNoteNode = forwardRef<any, StickyNoteNodeProps>(StickyNoteNodeInner);
